import * as React from 'react';
import { forwardRef, Ref } from 'react';
import { Button as MuiButton, CircularProgress } from '@material-ui/core';
import styled from 'styled-components';
import { downloadTableExcel } from 'modules/utils/excelExport';

// Sheets stay split the way react-export-excel split them, so exported files keep the same shape.
const EXCEL_PAGE_CHUNK_SIZE = 20000;

interface Props<T> {
  fileName: string;
  sheetName?: string;
  exportData: T[];
  width?: number;
  exportLoading?: boolean;
  onClickExport: () => void;
  buttonText?: string;
}

const ExcelExportButton = forwardRef(
  <T extends {}>(
    {
      fileName,
      exportData,
      sheetName = 'data',
      width = 100,
      exportLoading = false,
      onClickExport,
      buttonText = 'Export',
    }: Props<T>,
    ref: Ref<HTMLButtonElement>,
  ) => {
    // [EN] The hidden button is the download trigger: the parent fetches the rows, then clicks this
    //      ref once they arrive. Keeping that shape means no call site has to change.
    // [KO] 숨은 버튼이 다운로드 트리거다. 부모가 행을 받아온 뒤 이 ref 를 클릭한다.
    //      이 형태를 유지하면 호출부를 하나도 바꾸지 않아도 된다.
    const onDownload = async (): Promise<void> => {
      try {
        await downloadTableExcel({
          fileName,
          sheetName,
          exportData,
          chunkSize: EXCEL_PAGE_CHUNK_SIZE,
        });
      } catch (error) {
        console.error('Error exporting excel file:', error);
        window.alert('Failed to export excel file. Please try again later.');
      }
    };

    return (
      <>
        <Button
          width={width}
          variant={'contained'}
          onClick={onClickExport}
          size={'small'}
          disabled={exportData.length === 0 || exportLoading}
        >
          {exportLoading ? <CircularProgress size={22} thickness={5} /> : buttonText}
        </Button>
        <button hidden ref={ref} onClick={onDownload} />
      </>
    );
  },
);

export default ExcelExportButton;

const Button = styled(MuiButton)<{ width: number }>`
  width: ${props => props.width}px;
`;
